import { Node, mergeAttributes, JSONContent } from '@tiptap/core'
import { Plugin } from '@tiptap/pm/state'
import { storeToRefs } from 'pinia'

import { useEditorStore } from '@/stores'
import { DynamicNodeAttrs, IfBlockAttrs } from '@/types'

import { contentElementFiltered, handleKeyDownForBlocks } from './helpers'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    ifBlock: {
      insertIfBlock: (attrs: IfBlockAttrs, content?: JSONContent[] | undefined) => ReturnType
      openIfBlockDialog: (attrs?: IfBlockAttrs, callback?: (attrs: DynamicNodeAttrs) => void) => ReturnType
    }
  }
}

const ifBlockName = 'ifBlock'

function contentElement(dom: HTMLElement) {
  return contentElementFiltered(dom, 'data-if-edit')
}

export const IfBlock = Node.create({
  name: ifBlockName,

  group: 'block',
  content: 'block+',
  atom: false,
  draggable: false,

  addAttributes() {
    return {
      condition: {
        default: '',
        parseHTML: el => el.getAttribute('data-condition') || '',
        renderHTML: attrs => {
          if (!attrs.condition) return {}
          return { 'data-condition': attrs.condition }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-if-block="true"]',
        priority: 100,
        contentElement,
      },
      {
        tag: 'div[data-if-block]',
        priority: 50,
        contentElement,
      },
    ]
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-if-block': 'true',
        'data-condition': node.attrs.condition,
      }),
      // header handle (clicking opens dialog)
      [
        'div',
        {
          'data-if-edit': 'true',
          contenteditable: 'false',
        },
        `{% if ${node.attrs.condition} %}`,
      ],
      // content hole wrapper — editable as plain text
      ['div', 0],
      // footer handle (clicking opens dialog)
      [
        'div',
        {
          'data-if-edit': 'true',
          contenteditable: 'false',
        },
        '{% endif %}',
      ],
    ]
  },

  addCommands() {
    const editor = this.editor
    return {
      insertIfBlock:
        (attrs, content = [{ type: 'paragraph', content: [{ type: 'text', text: 'Your content...' }] }]) =>
        ({ chain }) =>
          chain()
            .insertContent({
              type: this.name,
              attrs,
              content,
            })
            .run(),
      openIfBlockDialog: (attrs, callback) => () => {
        const editorStore = useEditorStore()
        const { dynamicDialogType, dynamicDialogAttrs, dynamicDialogCallback } = storeToRefs(editorStore)
        dynamicDialogType.value = 'if'
        dynamicDialogAttrs.value = attrs
        dynamicDialogCallback.value = callback ?? (attrs => editor?.commands.insertIfBlock(attrs as IfBlockAttrs))
        return true
      },
    }
  },

  addProseMirrorPlugins() {
    const editor = this.editor
    return [
      new Plugin({
        props: {
          // (view, pos, node, nodePos, event, direct)
          handleClickOn(view, _pos, node, nodePos, event) {
            if (node.type.name !== ifBlockName || !editor.isEditable) return false
            const target = event?.target as HTMLElement | null
            if (!target) return false

            const handle = target.closest('[data-if-edit="true"]')
            const block = target.closest('[data-if-block="true"]')
            if (!handle || !block) return false

            editor?.commands.openIfBlockDialog(node.attrs as IfBlockAttrs, attrs => {
              view.dispatch(view.state.tr.setNodeMarkup(nodePos, undefined, attrs))
            })
            return true
          },
          handleKeyDown(view, event) {
            return handleKeyDownForBlocks(view, event)
          },
        },
      }),
    ]
  },
})
